import { Component, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { finalize, tap } from 'rxjs';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { MEDIA_TYPES } from '~app/app/~shared/constants/global.constants';
import { SharedModule } from '~app/app/~shared/modules/shared.module';
import { AdsTypeService } from '~app/app/~shared/services/ads-type.service';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { AdsType } from '~core/models/ads-type.models';
import { AdsTypeCreationDialogComponent } from '../../modals/ads-type-creation-dialog/ads-type-creation-dialog.component';

@Component({
  selector: 'ads-type-creation-form',
  templateUrl: './ads-type-creation-form.component.html',
  styleUrl: './ads-type-creation-form.component.scss',
  standalone: true,
  imports: [SharedModule]
})
export class AdsTypeCreationFormComponent
  extends BaseComponent
  implements OnInit
{
  private _adsTypeService = inject(AdsTypeService);
  private _dialogRef = inject(MatDialogRef<AdsTypeCreationDialogComponent>);
  private _resourceFacade = inject(ResourceFacade);

  adsType = input<AdsType>();

  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
        }, 500);
      }
    })
  );

  isSubmitting = signal(false);
  medias = MEDIA_TYPES;
  form = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required, Validators.minLength(3)]
    }),
    mediaType: new FormControl<string>('', {
      validators: [Validators.required]
    })
  });

  ngOnInit(): void {
    if (this.adsType()) {
      this.form.patchValue({
        name: this.adsType()?.name,
        mediaType: this.adsType()?.mediaType
      });
    }
  }

  onSubmit(): void {
    const values = this.form.value as AdsType;
    const id = this.adsType()?.id;

    this.isSubmitting.set(true);
    if (id) {
      this.update(id, values);
    } else {
      this.create(values);
    }
  }

  create(adsType: AdsType): void {
    this._adsTypeService
      .create(adsType)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close();
            this._snackbarService.open({
              text: `Type de publicité créé avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  update(id: string, adsType: AdsType): void {
    this._adsTypeService
      .update(id, adsType)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close();
            this._snackbarService.open({
              text: `Type de publicité modifié avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }
}
